import { Droppable } from "react-beautiful-dnd";
import {
  Box,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  CircularProgress,
  Button,
} from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { Search01Icon, Restaurant02Icon, ShoppingCart02Icon } from "@hugeicons/core-free-icons";
import { Content } from "@/types";
import { CATALOG_DROPPABLE } from "../hooks/useCatalogEditor";
import ProductCard from "./ProductCard";
import { EmptyState } from "@/components";
import { useTerminology } from "@/hooks";

const DroppableComp = Droppable as any;

interface Props {
  products: Content[];
  searchQuery: string;
  onSearchChange: (q: string) => void;
  isLoading: boolean;
  onCreateProductClick: () => void;
  onImportProductClick: () => void;
}

const ProductsColumn = ({
  products,
  searchQuery,
  onSearchChange,
  isLoading,
  onCreateProductClick,
  onImportProductClick,
}: Props) => {
  const { product, t } = useTerminology();
  const emptyIcon = searchQuery
    ? Search01Icon
    : product === "plato"
      ? Restaurant02Icon
      : ShoppingCart02Icon;
  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Stack spacing={1.5} sx={{ p: 2, pb: 1.5, height: 45 }}>
        <TextField
          placeholder="Buscar producto..."
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <HugeiconsIcon icon={Search01Icon} size={18} />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Divider />

      {/* Droppable area (just for source, clone mode) */}
      <DroppableComp droppableId={CATALOG_DROPPABLE} type="PRODUCT">
        {(provided: import("react-beautiful-dnd").DroppableProvided, snapshot: import("react-beautiful-dnd").DroppableStateSnapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 1.5,
              m: 1.5,
              border: "2px dashed",
              borderRadius: 2,
              borderColor: snapshot.isDraggingOver ? "primary.main" : "transparent",
              bgcolor: snapshot.isDraggingOver ? "action.hover" : "inherit",
              transition: "background-color 0.15s, border-color 0.15s",
            }}
          >
            {isLoading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                <CircularProgress size={28} />
              </Stack>
            ) : products.length === 0 ? (
              <EmptyState
                title={searchQuery ? "Sin resultados" : `No hay ${t("product", 2)}`}
                description={
                  searchQuery
                    ? `No encontramos ${t("product", 2)} para "${searchQuery}"`
                    : `No hay ${t("product", 2)} disponibles en este comercio`
                }
                icon={emptyIcon}
              />
            ) : (
              <Stack spacing={1}>
                {products.map((product, index) => (
                  <ProductCard key={product.id} product={product} index={index} />
                ))}
              </Stack>
            )}
            {provided.placeholder as any}
          </Box>
        )}
      </DroppableComp>

      <Divider />

      {/* Button to create and import products (fixed at the bottom) */}
      <Box sx={{ p: 1.5 }}>
        <Stack spacing={1}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            fullWidth
            onClick={onCreateProductClick}
            sx={{ textTransform: "capitalize" }}
            id="create-product-btn"
          >
            Crear {product}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="medium"
            fullWidth
            onClick={onImportProductClick}
            sx={{ textTransform: "capitalize" }}
            id="import-products-btn"
          >
            Importar {t("product", { count: 2, capitalize: true })}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
};

export default ProductsColumn;
